import { useEffect, useRef, useState } from 'react';

import { legalDocuments, legalPreamble } from '../legal';

/**
 * The three documents, in a dialog over the sign-up form.
 *
 * Over the form rather than a page away, because somebody halfway through a sign-up who follows
 * a link loses what they have typed — and a consent that costs you your work to read is one
 * nobody reads.
 *
 * All three are in one dialog with tabs rather than three links, because they are read together:
 * the privacy policy says what we do with personal data and the protection document says how,
 * and somebody checking one almost always wants the other.
 */
export function LegalDialog({
  openAt,
  onClose,
}: {
  /** Which document to show first. */
  openAt: string;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  /**
   * Which document is showing.
   *
   * Owned here rather than by the caller. The caller says which one to open on; once the dialog
   * is up, moving between the three is the dialog's own business and should not need the page
   * behind it to re-render.
   */
  const [showing, setShowing] = useState(openAt);

  // The list is a constant with three entries, but the compiler cannot know an index into it is
  // inhabited — so the fallback is a value rather than an index, and there is no undefined state
  // to render around.
  const active =
    legalDocuments.find((document) => document.key === showing) ??
    legalDocuments.find((document) => document.key === 'terms')!;

  // Focus moves into the dialog when it opens, and Escape closes it. Without both, somebody
  // using a keyboard is left tabbing through the form behind an overlay they cannot dismiss.
  useEffect(() => {
    closeRef.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="legal-scrim"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="legal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-title"
        ref={dialogRef}
      >
        <header className="legal__head">
          <div>
            <h2 id="legal-title">{active.title}</h2>
            <p className="legal__updated">Last updated {active.updated}</p>
          </div>

          <button
            type="button"
            className="legal__close"
            aria-label="Close"
            ref={closeRef}
            onClick={onClose}
          >
            ✕
          </button>
        </header>

        <nav className="legal__tabs" aria-label="Documents">
          {legalDocuments.map((document) => (
            <button
              key={document.key}
              type="button"
              className={`legal__tab${document.key === active.key ? ' legal__tab--on' : ''}`}
              onClick={() => setShowing(document.key)}
            >
              {document.title}
            </button>
          ))}
        </nav>

        <div className="legal__body">
          {/* Said once at the top rather than repeated in all three: which company, which
              product, and which of the two the reader is contracting with. */}
          <p className="legal__preamble">{legalPreamble}</p>

          <p className="legal__intro">{active.intro}</p>

          {active.sections.map((section) => (
            <section key={section.heading}>
              <h3>{section.heading}</h3>
              {section.body.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </section>
          ))}

          <p className="legal__note">
            This is a plain-language summary written to be read. A signed agreement, where your
            organisation needs one, is available on request.
          </p>
        </div>

        <footer className="legal__foot">
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}
